import { CommonModule } from '@angular/common';
import { Component, input, InputSignal, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { ParcelService } from '../../Entity/Parcel/parcel.service.upgrade';
import { AceLoaderComponent } from '../../UI-Lib/ACE/Loader/loader.component';
import { SharedPipesModule } from '../../UI-Lib/Pipes/shared-pipes.module';
import { SelectModule } from '../../UI-Lib/Select/select.module';
import { ActivityChartComponent } from './Activity-Chart/activityChart.component';
import { ActivityData, ActivityService } from './activity.service';
import { ActivityDeadlineFooterComponent } from './Deadline-Footer/activityDeadlineFooter.component';
import { ActivitySummaryTableComponent } from './Summary-Table/activitySummaryTable.component';
import { SiteService } from '../../Entity/Site/Site.Service.upgrade';
import { ActiveStatuses } from '../../constants.new';
import { takeUntil } from 'rxjs/operators';

import * as _ from 'lodash';

@Component({
    selector: 'activity-panel',
    templateUrl: 'activityPanel.component.html',
    styleUrls: ['activityPanel.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        AceLoaderComponent,
        SelectModule,
        SharedPipesModule,
        ActivityDeadlineFooterComponent,
        ActivitySummaryTableComponent,
        ActivityChartComponent,
    ],
})
export class ActivityPanelComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _activityService: ActivityService,
        private readonly _parcelService: ParcelService,
        private readonly _siteService: SiteService,
    ) {}

    siteId: InputSignal<number> = input();
    parcelId: InputSignal<number> = input();
    parcelYears: InputSignal<number[]> = input();

    parcels = [];
    annualData = [];
    availableYears: number[] = [];
    propertyCharacteristics = [];
    parcelFilterList = [];
    useLatestActualized: boolean;
    hasActiveOrPending: boolean;
    loading: boolean = true;
    selectedYear: number;
    activityData: ActivityData;

    private _destroy$: Subject<void> = new Subject();

    filterYears = (x) => x !== 'Latest';

    async ngOnInit(): Promise<void> {
        const storedParcelFilterList = sessionStorage[`ws_analysisIncludedParcels${this.siteId()}`];
        this.parcelFilterList = storedParcelFilterList ? JSON.parse(storedParcelFilterList) : [];

        this._activityService.userSessionFilterCache$.pipe(takeUntil(this._destroy$)).subscribe(async (cache) => {
            const cachedFilters = cache[this.siteId()];
            if (cachedFilters?.length && !_.isEqual(this.parcelFilterList, cachedFilters)) {
                this.parcelFilterList = cachedFilters;
                await this.getDataByYear();
            }
        });

        this._siteService.parcelList$.pipe(takeUntil(this._destroy$)).subscribe(async (parcelList) => {
            if (!parcelList && !this.parcelId()) {
                return;
            }

            if (parcelList && (!this.parcelFilterList || !this.parcelFilterList.length)) {
                this.hasActiveOrPending = parcelList.parcels.some(
                    (x) =>
                        x.activityStatusID === ActiveStatuses.ActivePending ||
                        x.activityStatusID === ActiveStatuses.Active,
                );
            }

            this.availableYears = await this.getYears();

            await this.getDataByYear(true);
            const maxYear = _.maxBy(this.annualData, 'year');
            if (maxYear) {
                this.selectedYear = maxYear.year;
            }
            if (!this.parcelFilterList.length) {
                this.parcelFilterList = this.parcels
                    .filter((x) => x.activityStatus === 'Active (Pending)' || x.activityStatus === 'Active')
                    .map((x) => x.parcelID);
                this.hasActiveOrPending = this.parcels.some((x) => x.activityStatusID === ActiveStatuses.Active);
            }
        });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async getYears(): Promise<number[]> {
        if (this.parcelYears()) {
            return this.parcelYears();
        } else {
            return await this._parcelService.getAvailableYears(this.siteId());
        }
    }

    async getDataByYear(useLatestActualized?: boolean): Promise<void> {
        this.loading = true;

        try {
            this.useLatestActualized = useLatestActualized;

            const data = this.parcelId()
                ? await this._activityService.getDataByParcel(
                      this.siteId(),
                      this.selectedYear,
                      this.parcelId(),
                      useLatestActualized,
                  )
                : await this._activityService.getDataByYear(
                      this.siteId(),
                      this.selectedYear,
                      this.parcelFilterList,
                      useLatestActualized,
                      this.hasActiveOrPending,
                  );

            this.processData(data);
        } finally {
            this.loading = false;
        }
    }

    processData(result: Core.AnalyticsDTO): void {
        this.activityData = result;

        if (this.parcelId()) {
            this.activityData.parcelFilterList = [this.parcelId()];
        } else {
            this.activityData.parcelFilterList = this.parcelFilterList; // we will need it if we want to load data for all years from within the table
        }

        this.activityData.siteId = this.siteId();
        this.activityData.useLatestActualized = this.useLatestActualized;

        this.activityData.availableYears = this.availableYears;

        this.parcels = result.parcels.map((element) => ({ ...element, ...{ isIncluded: true } }));

        this.annualData = result.annualData;
        this.propertyCharacteristics = result.propertyCharacteristics;
    }

    async openParcelFilter(): Promise<void> {
        const result = await this._activityService.openParcelFilter(this.parcels, this.parcelFilterList);
        if (!result) {
            return;
        }
        this._activityService.updateParcelFilterCacheForSite(this.siteId(), result);
    }

    getSummaryColumnClass(): string {
        return this.parcelId() ? 'col-sm-4' : 'col-sm-5';
    }

    getGraphColumnClass(): string {
        return this.parcelId() ? 'col-sm-8' : 'col-sm-7';
    }
}
